import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/* Enveloppe de champ : libellé, aide, message d'erreur                */
/* ------------------------------------------------------------------ */

export type FieldProps = {
  label?: string;
  aide?: string;
  erreur?: string;
  requis?: boolean;
  className?: string;
  htmlFor?: string;
  children: ReactNode;
};

export function Field({ label, aide, erreur, requis, className, htmlFor, children }: FieldProps) {
  return (
    <div className={cn('min-w-0', className)}>
      {label ? (
        <label className="etiquette" htmlFor={htmlFor}>
          {label}
          {requis ? <span className="ml-0.5 text-danger">*</span> : null}
        </label>
      ) : null}
      {children}
      {erreur ? (
        <p className="message-erreur" role="alert">
          {erreur}
        </p>
      ) : aide ? (
        <p className="aide">{aide}</p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Contrôles                                                           */
/* ------------------------------------------------------------------ */

export type InputProps = InputHTMLAttributes<HTMLInputElement> & { erreur?: boolean };

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, erreur, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={erreur || undefined}
      className={cn('champ', erreur && 'champ-erreur', className)}
      {...props}
    />
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { erreur?: boolean };

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, erreur, rows = 3, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={erreur || undefined}
      className={cn('champ resize-y', erreur && 'champ-erreur', className)}
      {...props}
    />
  );
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & { erreur?: boolean };

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, erreur, children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={erreur || undefined}
      className={cn('champ appearance-none bg-white pr-8', erreur && 'champ-erreur', className)}
      {...props}
    >
      {children}
    </select>
  );
});

/** Champ complet : libellé lié au contrôle par un identifiant généré. */
export function InputField({
  label,
  aide,
  erreur,
  requis,
  className,
  ...props
}: Omit<InputProps, 'erreur'> & Omit<FieldProps, 'children' | 'htmlFor'>) {
  const id = useId();
  return (
    <Field
      label={label}
      aide={aide}
      erreur={erreur}
      requis={requis}
      className={className}
      htmlFor={id}
    >
      <Input id={id} erreur={Boolean(erreur)} {...props} />
    </Field>
  );
}

export function SelectField({
  label,
  aide,
  erreur,
  requis,
  className,
  children,
  ...props
}: Omit<SelectProps, 'erreur'> & Omit<FieldProps, 'htmlFor'>) {
  const id = useId();
  return (
    <Field
      label={label}
      aide={aide}
      erreur={erreur}
      requis={requis}
      className={className}
      htmlFor={id}
    >
      <Select id={id} erreur={Boolean(erreur)} {...props}>
        {children}
      </Select>
    </Field>
  );
}

export function TextareaField({
  label,
  aide,
  erreur,
  requis,
  className,
  ...props
}: Omit<TextareaProps, 'erreur'> & Omit<FieldProps, 'children' | 'htmlFor'>) {
  const id = useId();
  return (
    <Field
      label={label}
      aide={aide}
      erreur={erreur}
      requis={requis}
      className={className}
      htmlFor={id}
    >
      <Textarea id={id} erreur={Boolean(erreur)} {...props} />
    </Field>
  );
}

/** Case à cocher alignée avec son libellé. */
export function Checkbox({
  label,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  const id = useId();
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-line text-mustard-500 accent-mustard-500
          focus:ring-2 focus:ring-mustard-500"
        {...props}
      />
      <label htmlFor={id} className="cursor-pointer select-none text-sm text-ink">
        {label}
      </label>
    </div>
  );
}
